import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

// Party-master typeahead for the Cash & Bank Day Book (mig 176).
// Reads /finance/daybook/payees — the SHARED pdc_payees master, the same record the PDC
// register and the payment-request spine resolve to. A vendor is one party whether it is
// paid by cheque or out of the drawer.
//
// Request-and-proceed (same pattern as FabricTypeahead): when the party is absent the user
// creates it inline (POST /finance/daybook/payees) and continues immediately. Nothing blocks.
// Creation is get-or-create on a case-insensitive name, so "LOCAL MARKET" typed twice can
// never mint two parties.
//
// Props:
//   value        — selected payee_id (or '')
//   initialLabel — display name when editing an existing entry (optional)
//   onChange(id, payeeObj) — fires on select / inline-create / clear
//   headers      — per-request config (the Day Book's X-Company-Id header)
//   placeholder, style, disabled, required
const box = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', width: '100%' };
const chip = (bg, fg) => ({ background: bg, color: fg, borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700, marginLeft: 6, whiteSpace: 'nowrap' });

export default function PartyTypeahead({ value, initialLabel, onChange, headers, placeholder, style, disabled }) {
  const [q, setQ] = useState(initialLabel || '');
  const [rows, setRows] = useState([]);
  const [sugg, setSugg] = useState([]);   // "did you mean" — near matches, kept SEPARATE (mig 188)
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value ? { id: value, name: initialLabel } : null);
  const [busy, setBusy] = useState(false);
  const wrap = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQ(initialLabel || ''); setSelected(value ? { id: value, name: initialLabel } : null); }, [value, initialLabel]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const search = (text) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.get('/finance/daybook/payees', { ...(headers || {}), params: text ? { q: text } : {} })
        .then(r => { setRows(r.data.data || []); setSugg(r.data.suggestions || []); })
        .catch(() => { setRows([]); setSugg([]); });
    }, 200);
  };

  const onType = (text) => {
    setQ(text); setOpen(true); setSelected(null);
    if (value) onChange('', null);   // typing clears a prior selection
    search(text);
  };

  const pick = (p) => { setSelected(p); setQ(p.name); setOpen(false); onChange(p.id, p); };

  // request-and-proceed: mint the party from the typed text, then select it and carry on.
  const createNew = (confirm_new = false) => {
    const name = q.trim();
    if (!name || busy) return;
    setBusy(true);
    api.post('/finance/daybook/payees', { name, ...(confirm_new ? { confirm_new: true } : {}) }, headers || {})
      .then(r => pick(r.data.data))
      .catch(e => {
        // NEAR-MATCH GUARD (mig 188). A 409 with near_match is the server saying a very
        // similar party already exists — the case-insensitive exact check cannot catch a
        // transposed letter, which is how GULSHAN KARYANA STORE / GULSHAN KARYAN STORE
        // both came to exist. Name them and make the operator choose; never auto-pick,
        // because a near match can genuinely be a different firm.
        const d = e.response?.data;
        if (e.response?.status === 409 && d?.near_match) {
          const list = (d.candidates || []).map(c => `  • ${c.name}${c.supplier_name ? `  (vendor ${c.supplier_name})` : ''}`).join('\n');
          if (window.confirm(
            `A party with a very similar name already exists:\n\n${list}\n\n`
            + `Cancel to pick the existing one, or OK to create "${name}" as a genuinely new party.`)) {
            createNew(true);
          }
          return;
        }
        window.alert(d?.error || 'Could not create party');
      })
      .finally(() => setBusy(false));
  };

  const clearSel = () => { setSelected(null); setQ(''); setRows([]); onChange('', null); };
  const exactExists = rows.some(r => (r.name || '').trim().toLowerCase() === q.trim().toLowerCase());

  return (
    <div ref={wrap} style={{ position: 'relative', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          value={q} disabled={disabled} placeholder={placeholder || 'Search the party master…'}
          onChange={e => onType(e.target.value)} onFocus={() => { setOpen(true); if (!rows.length) search(q); }}
          style={{ ...box, borderColor: selected ? '#16a34a' : '#ddd' }} />
        {selected && !disabled && (
          <button type="button" onClick={clearSel} title="Clear"
            style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 16, marginLeft: 4 }}>×</button>
        )}
      </div>
      {q.trim() && !selected && !disabled && (
        <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3 }}>Pick a party from the list, or create it.</div>
      )}
      {open && !disabled && (
        <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #ddd', borderRadius: 6, marginTop: 2, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {rows.map(p => (
            <div key={p.id} onClick={() => pick(p)}
              style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f2f2f2', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F7F7FB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <span style={{ fontSize: 13 }}>{p.name}</span>
              {/* mig-171 supplier link — tells the user this party is a known vendor, not a one-off name. */}
              {p.supplier_id && <span style={chip('#EAF7EE', '#166534')}>vendor</span>}
            </div>
          ))}
          {/* "DID YOU MEAN" — near matches in their OWN labelled block, below the exact hits
              and above Create, never mixed into the list and never auto-applied. This is what
              makes "avatr singh" reach AVTAR SINGH, which ILIKE '%q%' cannot do. */}
          {sugg.length > 0 && (
            <>
              <div style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                textTransform: 'uppercase', color: '#8A5A00', background: '#FEF3C7', borderTop: '1px solid #f2f2f2' }}>
                Did you mean — close matches already in the master
              </div>
              {sugg.map(p => (
                <div key={p.id} onClick={() => pick(p)}
                  style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f2f2f2',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFDF5' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FEF9E7'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FFFDF5'}>
                  <span style={{ fontSize: 13 }}>{p.name}</span>
                  <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {p.supplier_id && <span style={chip('#EAF7EE', '#166534')}>vendor</span>}
                    <span style={chip('#FEF3C7', '#8A5A00')}>{Math.round(Number(p.sim) * 100)}% match</span>
                  </span>
                </div>
              ))}
            </>
          )}
          {q.trim() && !exactExists && (
            <div onClick={() => createNew()}
              style={{ padding: '8px 10px', cursor: 'pointer', color: '#3B3A8F', fontWeight: 700, fontSize: 13, background: '#EEEDF8' }}>
              ＋ Create “{q.trim()}” and proceed <span style={{ fontWeight: 500, color: '#777' }}>(adds it to the shared party master)</span>
            </div>
          )}
          {!rows.length && !q.trim() && (
            <div style={{ padding: '8px 10px', color: '#999', fontSize: 12 }}>Type to search the party master…</div>
          )}
        </div>
      )}
    </div>
  );
}
